import { useState, useEffect, useRef } from 'react';
import { useAppNavigate } from '../contexts/PwaNavContext';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import './CustomerUserMenu.css';

export default function CustomerUserMenu() {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const { isAuthenticated, customer, logout } = useCustomerAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!isAuthenticated || !customer) {
    return (
      <div className="cum-wrap">
        <button
          className="cum-pill cum-pill--unauth"
          onClick={() => navigate('/passport')}
        >
          <div className="cum-avatar cum-avatar--unauth">!</div>
          <div className="cum-name cum-name--unauth">
            {t('foodPassport.notAuthed', 'Не авторизован')}
            <span className="cum-unauth-hint">{t('foodPassport.notAuthedHint', 'Баллы не сохранятся')}</span>
          </div>
        </button>
      </div>
    );
  }

  const firstName = customer.name?.split(' ')[0] || '';
  const initials = customer.name
    ?.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2) || 'U';

  return (
    <div className="cum-wrap" ref={ref}>
      <button className="cum-pill" onClick={() => setOpen(o => !o)}>
        <div className="cum-avatar">{initials}</div>
        <div className="cum-name">{firstName}</div>
        <span className={`cum-chevron ${open ? 'cum-chevron--open' : ''}`}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="cum-dropdown"
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          >
            <div className="cum-dropdown-header">
              <div className="cum-dropdown-name">{customer.name}</div>
              {customer.username && (
                <div className="cum-dropdown-username">@{customer.username}</div>
              )}
              <div className="cum-dropdown-points">
                {customer.loyaltyPoints || 0} {t('home.points')}
              </div>
            </div>
            <div className="cum-divider" />
            <button
              className="cum-item"
              onClick={() => { setOpen(false); navigate('/settings'); }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
              {t('common.settings', 'Settings')}
            </button>
            <button className="cum-item cum-item--disabled" disabled>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              {t('common.support', 'Support')}
            </button>
            <div className="cum-divider" />
            <button
              className="cum-item cum-item--danger"
              onClick={() => { setOpen(false); logout(); navigate('/'); }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              {t('common.logout', 'Log out')}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
